/**
 * The kiosk one-way door, RENDER-TIME half.
 *
 * `apps/web/src/history.js` guards `push`/`replace`, which covers every out-of-React
 * navigation — sagas, the API interceptor, notification handlers. It cannot cover three
 * things, because none of them calls either method:
 *
 *  1. **Browser BACK.** history v5 applies a POP and only then notifies listeners, so by
 *     the time anything could object the location has already changed. Measured: kiosk mode
 *     on, `/new-order` → `/kiosk`, `history.back()` lands on `/new-order`.
 *  2. **A cold boot in kiosk mode.** `enabled` is persisted, but nothing routes to `/kiosk`
 *     at startup — the app starts wherever the login flow sends it.
 *  3. **A stale bookmark or deep link** straight into a POS route.
 *
 * Wrapping the route tree answers all three in one place, and answers them better than an
 * interception could: the POS routes are never rendered, so `HomeLayout` — with its
 * notification polling, cash-register modals and keyboard shortcuts — does not mount in
 * front of a customer for even one frame.
 *
 * While kiosk mode is off this is a boolean read and a passthrough; the POS is untouched.
 *
 * See docs/KIOSK_ARCHITECTURE.md §13.10 and §13.12.
 */
import React from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { shouldLockToKiosk } from "@rista/core/kiosk/navigation/routes";
import { ROUTE_CONSTANTS_MAP } from "utils/routeConstants";

const KioskLock: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const kioskModeEnabled = useSelector((state: any) => state?.kioskConfigStore?.enabled);
  const { pathname } = useLocation();

  // `replace`, not a push: the entry that got here must not survive for another back
  // gesture to walk into, or a customer holding back would ping-pong through the POS.
  if (shouldLockToKiosk(kioskModeEnabled, pathname, ROUTE_CONSTANTS_MAP.KIOSK_ROUTE)) {
    return <Navigate to={ROUTE_CONSTANTS_MAP.KIOSK_ROUTE} replace />;
  }

  return <>{children}</>;
};

export default KioskLock;
